/**
 * useFontLoadTracking - Track Adobe Fonts Load Times in PostHog
 * Phase 6: Performance Monitoring
 *
 * Purpose: Monitor font loading performance per tier
 * Tracks: Inter (all tiers), Neue Haas Grotesk Display (Layer 2+), Miller Display (Layer 3)
 *
 * Font Loading Strategy:
 * - All fonts use `font-display: swap` (best practice for web fonts)
 * - Adobe Typekit handles automatic subsetting and WOFF2 compression
 * - Network payload: <50KB per font (optimized by Adobe)
 *
 * Analytics Events:
 * - Font_Loaded (with font_family, load_time_ms, tier)
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useTier } from './useTier';
import { addBreadcrumb } from '../lib/analytics';

/**
 * Track font loading performance and send to PostHog
 * Only runs on web platform
 *
 * Performance Fix (2025-12-09): Uses useRef pattern to prevent duplicate
 * listener registration on tier changes. Listeners initialize once per page
 * load, tier value captured at event time via ref.
 */
export function useFontLoadTracking() {
  const { tierLevel } = useTier();
  const tierLevelRef = useRef(tierLevel);

  // Update ref when tier changes (no effect re-run)
  useEffect(() => {
    tierLevelRef.current = tierLevel;
  }, [tierLevel]);

  useEffect(() => {
    // Only run on web platform
    if (Platform.OS !== 'web') {
      console.log('[FontTracking] Skipping monitoring - not running on web');
      return;
    }

    // Check if document.fonts API is available
    if (typeof document === 'undefined' || !('fonts' in document)) {
      console.log('[FontTracking] Skipping monitoring - document.fonts API not available');
      return;
    }

    console.log('[FontTracking] Starting font load monitoring');

    // Fonts to track based on tier
    const fontsToTrack = [
      'Inter', // Body font (all tiers)
      'neue-haas-grotesk-display', // Display font Layer 1-2
      'miller-display', // Editorial serif Layer 3
    ];

    fontsToTrack.forEach((fontFamily) => {
      const startTime = performance.now();

      /**
       * Font Load Timeout Wrapper
       * Prevents fontfaceobserver 6000ms timeout from reaching Sentry
       * Strategy: Race between font load and 5000ms timeout (under 6000ms threshold)
       * Gracefully degrades to fallback fonts if timeout occurs
       */
      const fontLoadPromise = document.fonts.ready;
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve('timeout'), 5000); // 5s timeout (before fontfaceobserver's 6s)
      });

      Promise.race([fontLoadPromise, timeoutPromise]).then((result) => {
        const loadTime = performance.now() - startTime;

        if (result === 'timeout') {
          // Timeout occurred - font may still be loading but we won't wait longer
          console.warn('[FontTracking]', fontFamily, 'timeout after', Math.round(loadTime), 'ms');

          // Font_Load_Timeout retired (#797) -- Sentry breadcrumb
          addBreadcrumb('font.load', 'Font timeout: ' + fontFamily, { font_family: fontFamily, timeout_ms: Math.round(loadTime) }, 'warning');
          return; // Exit early, fallback fonts will be used
        }

        // Check if specific font is loaded
        const fontLoaded = document.fonts.check(`1rem ${fontFamily}`);

        if (fontLoaded) {
          console.log('[FontTracking]', fontFamily, 'loaded in', Math.round(loadTime), 'ms');

          // Font_Loaded retired (#797)
        } else {
          console.warn('[FontTracking]', fontFamily, 'not loaded after', Math.round(loadTime), 'ms');

          // Font_Load_Failed retired (#797) -- Sentry breadcrumb
          addBreadcrumb('font.load', 'Font failed: ' + fontFamily, { font_family: fontFamily }, 'error');
        }
      });
    });

    // Optional: Track individual font loading events (more granular)
    // This catches fonts as they load, not waiting for all to be ready
    const trackFontLoad = (fontFace: FontFace) => {
      const fontFamily = fontFace.family;
      console.log('[FontTracking] Font loaded:', fontFamily, fontFace.status);

      // Font_Individual_Load retired (#797)
    };

    // Listen for font loading events
    document.fonts.addEventListener('loadingdone', (event) => {
      event.fontfaces.forEach(trackFontLoad);
    });

    // Cleanup listener on unmount
    return () => {
      document.fonts.removeEventListener('loadingdone', trackFontLoad as any);
    };
  }, []); // ← CRITICAL FIX: Empty dependency array prevents re-initialization
}
